import * as React from "react";
import styled from "styled-components";
import { useMemo, useCallback } from "react";
import { StaticColor } from "views/logic/views/formatting/highlighting/HighlightingColor";
import { ColorPickerPopover, Icon } from "components/common";
import { DEFAULT_CUSTOM_HIGHLIGHT_RANGE } from "views/Constants";
import { conditionToExprMapper, exprToConditionMapper } from "views/logic/ExpressionConditionMappers";
import useViewsSelector from "views/stores/useViewsSelector";
import { selectHighlightingRules } from "views/logic/slices/highlightSelectors";
import { updateHighlightingRule, createHighlightingRules } from "views/logic/slices/highlightActions";
import { randomColor } from "views/logic/views/formatting/highlighting/HighlightingRule";
import useViewsDispatch from "views/stores/useViewsDispatch";
import NoAttributeProvided from "components/event-definitions/replay-search/NoAttributeProvided";
import useReplaySearchContext from "components/event-definitions/replay-search/hooks/useReplaySearchContext";
import useAlertAndEventDefinitionData from "./hooks/useAlertAndEventDefinitionData";
const List = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
`;
const Condition = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;
const ColorComponent = styled.div`
  width: 13px;
  height: 13px;
  border-radius: 2px;
  cursor: pointer;
`;
const useHighlightingRules = () => useViewsSelector(selectHighlightingRules);
const AggregationConditions = () => {
  const dispatch = useViewsDispatch();
  const { alertId, definitionId } = useReplaySearchContext();
  const { aggregations } = useAlertAndEventDefinitionData(alertId, definitionId);
  const highlightingRules = useHighlightingRules();
  const aggregationsMap = useMemo(
    () => Object.fromEntries(aggregations.map((agg) => [`${agg.fnSeries}${agg.expr}${agg.value}`, agg])),
    [aggregations]
  );
  const changeColor = useCallback(
    ({ rule, newColor, condition }) => {
      if (rule) {
        dispatch(updateHighlightingRule(rule, { color: StaticColor.create(newColor) }));
      } else {
        const { value, fnSeries, expr } = aggregationsMap[condition];
        dispatch(
          createHighlightingRules([
            {
              value,
              field: fnSeries,
              color: randomColor(),
              condition: exprToConditionMapper[expr]
            }
          ])
        );
      }
    },
    [aggregationsMap, dispatch]
  );
  const validAggregations = aggregations.map(({ fnSeries, value, expr }) => `${fnSeries}${expr}${value}`);
  const highlightedAggregations = useMemo(
    () => Object.fromEntries(
      highlightingRules.map((rule) => {
        const { field, value, condition } = rule;
        const expr = conditionToExprMapper[condition];
        if (expr) {
          const key = `${field}${expr}${value}`;
          return [key, rule];
        }
        return void 0;
      }).filter((rule) => rule !== void 0 && validAggregations.includes(rule[0]))
    ),
    [highlightingRules, validAggregations]
  );
  return Object.keys(highlightedAggregations).length ? /* @__PURE__ */ React.createElement(List, null, Object.entries(highlightedAggregations).map(([condition, rule]) => {
    const color = rule == null ? void 0 : rule.color;
    const hexColor = color == null ? void 0 : color.color;
    return /* @__PURE__ */ React.createElement(Condition, { title: condition, key: condition }, /* @__PURE__ */ React.createElement(
      ColorPickerPopover,
      {
        id: "formatting-rule-color",
        placement: "right",
        color: hexColor,
        colors: DEFAULT_CUSTOM_HIGHLIGHT_RANGE.map((c) => [c]),
        triggerNode: /* @__PURE__ */ React.createElement(ColorComponent, { style: { backgroundColor: hexColor }, "data-testid": `color-${condition}` }, !hexColor && /* @__PURE__ */ React.createElement(Icon, { name: "colors", size: "xs" })),
        onChange: (newColor, _, hidePopover) => {
          hidePopover();
          changeColor({ newColor, rule, condition });
        }
      }
    ), /* @__PURE__ */ React.createElement("span", null, condition));
  })) : /* @__PURE__ */ React.createElement(NoAttributeProvided, null);
};
export default AggregationConditions;
